import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Users,
  UserPlus,
  TrendingUp,
  Download,
  LogOut,
  Search,
  Phone,
  Copy,
  X,
  Webhook,
  Save,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "Nouveau", label: "Nouveau", color: "bg-blue-100 text-blue-800" },
  { value: "Contacté", label: "Contacté", color: "bg-yellow-100 text-yellow-800" },
  { value: "Devis envoyé", label: "Devis envoyé", color: "bg-purple-100 text-purple-800" },
  { value: "Gagné", label: "Gagné", color: "bg-green-100 text-green-800" },
  { value: "Perdu", label: "Perdu", color: "bg-red-100 text-red-800" },
];

const URGENCE_OPTIONS = [
  { value: "all", label: "Toutes les urgences" },
  { value: "Dans la semaine", label: "Dans la semaine" },
  { value: "Dans le mois", label: "Dans le mois" },
  { value: "Pas urgent", label: "Pas urgent" },
];

const VERTICAL_OPTIONS = [
  { value: "all", label: "Toutes les pages" },
  { value: "toiture", label: "Toiture" },
  { value: "climatisation", label: "Climatisation" },
  { value: "photovoltaique", label: "Photovoltaïque" },
  { value: "general", label: "Accueil" },
];

const VERTICAL_BADGES = {
  toiture: { label: "Toiture", color: "bg-amber-100 text-amber-800" },
  climatisation: { label: "Clim", color: "bg-sky-100 text-sky-800" },
  photovoltaique: { label: "PV", color: "bg-emerald-100 text-emerald-800" },
  general: { label: "Accueil", color: "bg-slate-100 text-slate-700" },
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total_month: 0, new_leads: 0, conversion_rate: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [filters, setFilters] = useState({
    statut: "all",
    urgence: "all",
    search: "",
  });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookSaving, setWebhookSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/settings/webhook`, { withCredentials: true })
      .then(({ data }) => setWebhookUrl(data.webhook_url || ""))
      .catch(() => {});
  }, []);

  const handleWebhookSave = async () => {
    setWebhookSaving(true);
    try {
      await axios.put(
        `${API}/settings/webhook`,
        { webhook_url: webhookUrl },
        { withCredentials: true }
      );
      toast.success("URL du webhook enregistrée");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setWebhookSaving(false);
    }
  };

  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.statut && filters.statut !== "all") params.append("statut", filters.statut);
      if (filters.urgence && filters.urgence !== "all") params.append("urgence", filters.urgence);
      if (filters.vertical && filters.vertical !== "all") params.append("vertical", filters.vertical);
      if (filters.search) params.append("search", filters.search);

      const { data } = await axios.get(`${API}/leads?${params.toString()}`, {
        withCredentials: true,
      });
      setLeads(data);
    } catch (error) {
      console.error("Error fetching leads:", error);
      if (error.response?.status === 401) {
        navigate("/admin/login");
      }
    }
  }, [filters, navigate]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/leads/stats`, {
        withCredentials: true,
      });
      setStats(data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLeads(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchLeads, fetchStats]);

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.patch(
        `${API}/leads/${leadId}`,
        { statut: newStatus },
        { withCredentials: true }
      );
      setLeads((prev) =>
        prev.map((lead) =>
          lead.id === leadId ? { ...lead, statut: newStatus } : lead
        )
      );
      if (selectedLead?.id === leadId) {
        setSelectedLead((prev) => ({ ...prev, statut: newStatus }));
      }
      fetchStats();
      toast.success("Statut mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleNotesChange = async (leadId, notes) => {
    try {
      await axios.patch(
        `${API}/leads/${leadId}`,
        { notes },
        { withCredentials: true }
      );
      setLeads((prev) =>
        prev.map((lead) => (lead.id === leadId ? { ...lead, notes } : lead))
      );
      toast.success("Notes sauvegardées");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/leads/export`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "lldls_leads.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export téléchargé");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const copyPhone = (phone) => {
    navigator.clipboard.writeText(phone);
    toast.success("Numéro copié !");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-BE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find((s) => s.value === status);
    return option ? option.color : "bg-gray-100 text-gray-800";
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_lead-capture-roof/artifacts/593h2yec_image.png"
                alt="LLDLS"
                className="h-10"
              />
              <span className="font-heading font-semibold hidden sm:block">
                Dashboard Admin
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-slate-300 text-sm hidden md:block">
                {user?.email}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-300 hover:text-white hover:bg-slate-800"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card data-testid="stat-total-month">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                Leads ce mois
              </CardTitle>
              <Users className="w-5 h-5 text-slate-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">
                {stats.total_month}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-new-leads">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                Leads Nouveau
              </CardTitle>
              <UserPlus className="w-5 h-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {stats.new_leads}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-conversion">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                Taux de conversion
              </CardTitle>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {stats.conversion_rate}%
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Webhook Settings */}
        <Card className="mb-8" data-testid="webhook-settings-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Webhook className="w-5 h-5 text-amber-500" />
              Webhook speed-to-lead
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-3">
              À chaque nouveau lead (avec consentement RGPD), une requête POST JSON est envoyée
              à cette URL — en parallèle des notifications, sans bloquer la soumission.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                type="url"
                placeholder="https://votre-outil.com/webhook/..."
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                className="flex-1"
                data-testid="webhook-url-input"
              />
              <Button
                onClick={handleWebhookSave}
                disabled={webhookSaving}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="webhook-save-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {webhookSaving ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par nom ou code postal..."
                value={filters.search}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select
              value={filters.statut}
              onValueChange={(value) =>
                setFilters((prev) => ({ ...prev, statut: value }))
              }
            >
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.urgence}
              onValueChange={(value) =>
                setFilters((prev) => ({ ...prev, urgence: value }))
              }
            >
              <SelectTrigger className="w-full sm:w-48" data-testid="urgence-filter">
                <SelectValue placeholder="Toutes les urgences" />
              </SelectTrigger>
              <SelectContent>
                {URGENCE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.vertical}
              onValueChange={(value) =>
                setFilters((prev) => ({ ...prev, vertical: value }))
              }
            >
              <SelectTrigger className="w-full sm:w-48" data-testid="vertical-filter">
                <SelectValue placeholder="Toutes les pages" />
              </SelectTrigger>
              <SelectContent>
                {VERTICAL_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={handleExport}
              variant="outline"
              className="flex items-center gap-2"
              data-testid="export-button"
            >
              <Download className="w-4 h-4" />
              Exporter CSV
            </Button>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto"></div>
            </div>
          ) : leads.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              Aucun lead trouvé
            </div>
          ) : (
            <Table data-testid="leads-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold">Nom</TableHead>
                  <TableHead className="font-semibold">Vertical</TableHead>
                  <TableHead className="font-semibold">Téléphone</TableHead>
                  <TableHead className="font-semibold hidden md:table-cell">Email</TableHead>
                  <TableHead className="font-semibold hidden lg:table-cell">Code Postal</TableHead>
                  <TableHead className="font-semibold hidden lg:table-cell">Projet</TableHead>
                  <TableHead className="font-semibold hidden xl:table-cell">Urgence</TableHead>
                  <TableHead className="font-semibold">Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow
                    key={lead.id}
                    className="cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setSelectedLead(lead)}
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <TableCell className="text-sm text-slate-600">
                      {formatDate(lead.created_at)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {lead.prenom} {lead.nom}
                    </TableCell>
                    <TableCell data-testid={`lead-vertical-${lead.id}`}>
                      {VERTICAL_BADGES[lead.vertical] ? (
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${VERTICAL_BADGES[lead.vertical].color}`}>
                          {VERTICAL_BADGES[lead.vertical].label}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Phone className="w-3 h-3 text-slate-400" />
                        <span className="text-sm">{lead.telephone}</span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-slate-600">
                      {lead.email}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">
                      {lead.code_postal}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">
                      {lead.type_projet}
                    </TableCell>
                    <TableCell className="hidden xl:table-cell text-sm">
                      {lead.urgence}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Select
                        value={lead.statut}
                        onValueChange={(value) => handleStatusChange(lead.id, value)}
                      >
                        <SelectTrigger
                          className={`w-36 text-xs font-medium ${getStatusBadge(lead.statut)}`}
                          data-testid={`status-select-${lead.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      {/* Lead Detail Sheet */}
      <Sheet open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center justify-between">
              <span>
                {selectedLead?.prenom} {selectedLead?.nom}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedLead(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </SheetTitle>
          </SheetHeader>

          {selectedLead && (
            <div className="mt-6 space-y-6" data-testid="lead-detail-panel">
              {/* Contact Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900">Coordonnées</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <div className="text-xs text-slate-500">Téléphone</div>
                      <div className="font-medium">{selectedLead.telephone}</div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyPhone(selectedLead.telephone)}
                      data-testid="copy-phone-button"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copier
                    </Button>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-xs text-slate-500">Email</div>
                    <div className="font-medium">{selectedLead.email}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-xs text-slate-500">Code Postal</div>
                    <div className="font-medium">{selectedLead.code_postal}</div>
                  </div>
                </div>
              </div>

              {/* Project Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900">Projet</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-xs text-slate-500">Page d'origine</div>
                    <div className="font-medium text-sm">
                      {VERTICAL_BADGES[selectedLead.vertical]?.label || "—"}
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-xs text-slate-500">Type de projet</div>
                    <div className="font-medium text-sm">{selectedLead.type_projet}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-xs text-slate-500">Type de bien</div>
                    <div className="font-medium text-sm">{selectedLead.type_bien}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-xs text-slate-500">Surface</div>
                    <div className="font-medium text-sm">{selectedLead.surface}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-xs text-slate-500">Urgence</div>
                    <div className="font-medium text-sm">{selectedLead.urgence}</div>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900">Statut</h3>
                <Select
                  value={selectedLead.statut}
                  onValueChange={(value) => handleStatusChange(selectedLead.id, value)}
                >
                  <SelectTrigger data-testid="detail-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900">Notes</h3>
                <Textarea
                  placeholder="Ajouter des notes sur ce lead..."
                  value={selectedLead.notes || ""}
                  onChange={(e) =>
                    setSelectedLead((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  onBlur={() =>
                    handleNotesChange(selectedLead.id, selectedLead.notes)
                  }
                  rows={4}
                  data-testid="notes-textarea"
                />
              </div>

              {/* Metadata */}
              <div className="pt-4 border-t border-slate-200 text-sm text-slate-500">
                <div>Créé le : {formatDate(selectedLead.created_at)}</div>
                <div>Source : {selectedLead.source}</div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AdminDashboard;
